/**
 * Runtime Configuration Schema
 *
 * Single source of truth for runtime configuration keys.
 * These are client-safe environment variables (VITE_ prefixed)
 * that can be injected at runtime via index.html.
 */

/**
 * All supported runtime configuration keys.
 * Server reads VITE_{KEY} from process.env and injects into HTML.
 */
export const RUNTIME_CONFIG_KEYS = [
    // Auth0 Configuration
    'AUTH0_DOMAIN',
    'AUTH0_CLIENT_ID',
    'AUTH0_AUDIENCE',

    // Environment
    'ENV',
    // Deployment identity for telemetry (preflight vs staging); ENV stays the behaviour switch.
    'DEPLOY_ENV',

    // AWS S3
    'S3_PUBLIC_BUCKET_URL',

    // Third-party Services
    'TURNSTILE_SITE_KEY',
    'STRIPE_PUBLISHABLE_KEY',

    // Analytics
    'PRODUCT_FRUITS_WORKSPACE_CODE',
    'PUBLIC_POSTHOG_KEY',
    'PUBLIC_POSTHOG_HOST',
    'PUBLIC_POSTHOG_UI_HOST',

    // Feature Flags
    'REACT_GRAB_ENABLED',
] as const;

/**
 * Type for valid runtime configuration keys
 */
export type RuntimeConfigKey = (typeof RUNTIME_CONFIG_KEYS)[number];

/**
 * Keys that are intentionally unset in some deployments.
 * DEPLOY_ENV is only injected by the preflight Worker; elsewhere the client falls back to ENV.
 */
export const OPTIONAL_RUNTIME_CONFIG_KEYS = ['DEPLOY_ENV'] as const satisfies readonly RuntimeConfigKey[];

/**
 * Type for the complete runtime configuration object
 */
export type RuntimeConfig = Record<RuntimeConfigKey, string>;

/**
 * Placeholder marker for HTML injection.
 * Server replaces this with the actual config script.
 */
export const RUNTIME_CONFIG_PLACEHOLDER = '<!--__RUNTIME_CONFIG_INJECTION_POINT__-->';

/**
 * Window property name for runtime config
 */
export const RUNTIME_CONFIG_WINDOW_KEY = '__RUNTIME_CONFIG__' as const;
